import { useState, useEffect, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Loader2, Factory, Landmark, Package, ShoppingCart,
  TrendingUp, AlertTriangle, CheckCircle,
  ArrowRight, BarChart3, DollarSign, Truck, Users,
  Activity, Clock, Calendar, RefreshCw, Database,
  Building2, CircleAlert, Wifi, WifiOff,
  Boxes,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";
import AuthContext from "@/context/AuthContext";

import KPIScorecard from "@/components/crossfunction/KPIScorecard";
import TrendAnalysis from "@/components/crossfunction/TrendAnalysis";
import HealthScores from "@/components/crossfunction/HealthScores";
import RiskMatrix from "@/components/crossfunction/RiskMatrix";
import EfficiencyMetrics from "@/components/crossfunction/EfficiencyMetrics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOURCE_PRESENTATION = {
  sap_live: {
    label: "Live SAP",
    badge: "border-emerald-200 bg-emerald-50 text-emerald-700",
    dot: "bg-emerald-500",
    description: "Latest values supplied by the active SAP connection",
  },
  application_data: {
    label: "Application Data",
    badge: "border-blue-200 bg-blue-50 text-blue-700",
    dot: "bg-blue-500",
    description: "Saved planning data from this application",
  },
  demo: {
    label: "Sample Data",
    badge: "border-amber-200 bg-amber-50 text-amber-700",
    dot: "bg-amber-500",
    description: "Demonstration values; not live SAP data",
  },
  mock: {
    label: "Sample Data",
    badge: "border-amber-200 bg-amber-50 text-amber-700",
    dot: "bg-amber-500",
    description: "Demonstration values; not live SAP data",
  },
  no_connection: {
    label: "No SAP Connection",
    badge: "border-slate-200 bg-slate-50 text-slate-600",
    dot: "bg-slate-400",
    description: "No active SAP connection is configured",
  },
  sap_no_records: {
    label: "No SAP Records",
    badge: "border-amber-200 bg-amber-50 text-amber-700",
    dot: "bg-amber-500",
    description: "SAP returned no records for the selected scope",
  },
  sap_timeout: {
    label: "SAP Timeout",
    badge: "border-red-200 bg-red-50 text-red-700",
    dot: "bg-red-500",
    description: "The SAP request did not finish in time",
  },
  sap_unauthorized: {
    label: "Credentials Rejected",
    badge: "border-red-200 bg-red-50 text-red-700",
    dot: "bg-red-500",
    description: "SAP rejected the configured credentials",
  },
  sap_forbidden: {
    label: "Authorization Required",
    badge: "border-red-200 bg-red-50 text-red-700",
    dot: "bg-red-500",
    description: "The SAP user lacks authorization for this data",
  },
  sap_service_not_found: {
    label: "Service Not Found",
    badge: "border-red-200 bg-red-50 text-red-700",
    dot: "bg-red-500",
    description: "The required SAP service is unavailable",
  },
  sap_invalid_query: {
    label: "Query / Mapping Error",
    badge: "border-red-200 bg-red-50 text-red-700",
    dot: "bg-red-500",
    description: "The SAP query or response mapping needs attention",
  },
  sap_unreachable: {
    label: "SAP Unreachable",
    badge: "border-red-200 bg-red-50 text-red-700",
    dot: "bg-red-500",
    description: "The active SAP system cannot currently be reached",
  },
};

const DEFAULT_SOURCE = {
  label: "Data Unavailable",
  badge: "border-slate-200 bg-slate-50 text-slate-600",
  dot: "bg-slate-400",
  description: "No verified data source is currently available",
};

const toFiniteNumber = (value) => {
  if (value == null || value === "") return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const fmt = (value) => {
  const numeric = toFiniteNumber(value);
  if (numeric == null) return "—";
  const abs = Math.abs(numeric);
  if (abs >= 1_00_00_000) return `${(numeric / 1_00_00_000).toFixed(1)} Cr`;
  if (abs >= 1_00_000) return `${(numeric / 1_00_000).toFixed(1)} L`;
  return numeric.toLocaleString("en-IN");
};

const fmtWithSuffix = (value, suffix, fallback = "—") => {
  const numeric = toFiniteNumber(value);
  return numeric == null ? fallback : `${numeric.toLocaleString("en-IN")}${suffix}`;
};

const sourceMeta = (source) => SOURCE_PRESENTATION[source] || DEFAULT_SOURCE;

const HealthIndicator = ({ value, thresholds, type = "higher" }) => {
  const numeric = toFiniteNumber(value);
  if (numeric == null) return <span className="h-2 w-2 rounded-full bg-slate-300" aria-label="No KPI value" />;

  const isGood = type === "higher" ? numeric >= thresholds[0] : numeric <= thresholds[0];
  const isWarning = type === "higher" ? numeric >= thresholds[1] : numeric <= thresholds[1];
  const Icon = isGood ? CheckCircle : AlertTriangle;
  const color = isGood ? "text-emerald-600" : isWarning ? "text-amber-500" : "text-red-500";
  const label = isGood ? "Within target" : isWarning ? "Needs attention" : "Outside target";

  return <Icon className={`h-4 w-4 ${color}`} aria-label={label} />;
};

const SummaryCard = ({ label, value, detail, icon: Icon, tone }) => {
  const toneStyles = {
    blue: "border-blue-100 bg-gradient-to-br from-white to-blue-50/80 text-blue-600 shadow-blue-100/50",
    emerald: "border-emerald-100 bg-gradient-to-br from-white to-emerald-50/80 text-emerald-600 shadow-emerald-100/50",
    amber: "border-amber-100 bg-gradient-to-br from-white to-amber-50/80 text-amber-600 shadow-amber-100/50",
    violet: "border-violet-100 bg-gradient-to-br from-white to-violet-50/80 text-violet-600 shadow-violet-100/50",
  };
  const accent = toneStyles[tone] || toneStyles.blue;

  return (
    <Card className={`overflow-hidden border shadow-sm ${accent}`}>
      <CardContent className="relative flex min-h-28 items-start gap-3 p-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-current/10">
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0 text-slate-950">
          <p className="text-xs font-medium text-slate-500">{label}</p>
          <p className="mt-1 truncate text-2xl font-bold tracking-tight">{value}</p>
          <p className="mt-1 text-[11px] text-slate-500">{detail}</p>
        </div>
        <div className="absolute bottom-3 right-4 h-7 w-24 rounded-full bg-current/10 blur-[1px]" aria-hidden="true" />
      </CardContent>
    </Card>
  );
};

const ModuleCard = ({ title, icon: Icon, color, bgColor, accentColor, metrics, source, onClick }) => {
  const presentation = sourceMeta(source);
  const visualStyle = {
    "Production Planning": {
      card: "border-blue-100 bg-gradient-to-br from-white via-white to-blue-50/80",
      metric: "border-blue-100/80 bg-blue-50/55",
      action: "bg-gradient-to-r from-blue-50 to-blue-100/80",
    },
    "Financial Analysis": {
      card: "border-emerald-100 bg-gradient-to-br from-white via-white to-emerald-50/80",
      metric: "border-emerald-100/80 bg-emerald-50/55",
      action: "bg-gradient-to-r from-emerald-50 to-emerald-100/80",
    },
    "Supply Chain": {
      card: "border-violet-100 bg-gradient-to-br from-white via-white to-violet-50/80",
      metric: "border-violet-100/80 bg-violet-50/55",
      action: "bg-gradient-to-r from-violet-50 to-violet-100/80",
    },
    Purchasing: {
      card: "border-orange-100 bg-gradient-to-br from-white via-white to-orange-50/80",
      metric: "border-orange-100/80 bg-orange-50/55",
      action: "bg-gradient-to-r from-orange-50 to-orange-100/80",
    },
  }[title] || {
    card: "border-slate-200 bg-white",
    metric: "border-slate-100 bg-slate-50/70",
    action: "bg-slate-50",
  };

  return (
    <Card
      className={`group relative flex h-full cursor-pointer flex-col overflow-hidden shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${visualStyle.card}`}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick();
        }
      }}
      role="link"
      tabIndex={0}
      data-testid={`overview-module-${title.toLowerCase().replace(/\s/g, "-")}`}
    >
      <span className={`absolute inset-y-0 left-0 w-1 ${accentColor}`} />
      <CardHeader className="p-4 pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${bgColor}`}>
              <Icon className={`h-5 w-5 ${color}`} />
            </div>
            <div className="min-w-0">
              <CardTitle className="truncate text-base text-slate-950">{title}</CardTitle>
              <p className="mt-1 line-clamp-1 text-xs text-slate-500">{presentation.description}</p>
            </div>
          </div>
          <Badge variant="outline" className={`shrink-0 gap-1.5 text-[10px] font-medium ${presentation.badge}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${presentation.dot}`} />
            {presentation.label}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col p-4 pt-1">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {metrics.slice(0, 2).map((metric) => (
            <div key={metric.label} className={`rounded-lg border p-3 ${visualStyle.metric}`}>
              <div className="flex items-center gap-1.5 text-xs text-slate-500">
                {metric.icon && <metric.icon className="h-3.5 w-3.5" />}
                <span className="truncate">{metric.label}</span>
              </div>
              <div className="mt-1.5 flex items-center gap-1.5 text-sm font-semibold text-slate-950">
                {metric.health && <HealthIndicator {...metric.health} />}
                <span className="truncate">{metric.value}</span>
              </div>
            </div>
          ))}
        </div>
        <div className={`mt-auto flex items-center justify-between rounded-lg px-3 py-2.5 ${visualStyle.action}`}>
          <span className="text-xs text-slate-600">View operational details</span>
          <span className={`flex items-center gap-1 text-xs font-semibold ${color}`}>
            View details <ArrowRight className="h-3.5 w-3.5" />
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

const ExecutiveOverview = () => {
  const [overviewData, setOverviewData] = useState(null);
  const [crossData, setCrossData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [overviewError, setOverviewError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [crossLoading, setCrossLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [periodDays, setPeriodDays] = useState(90);
  const { hasRole } = useContext(AuthContext);
  const navigate = useNavigate();

  const fetchOverview = useCallback(async (notify = false, targetPeriod = periodDays) => {
    setRefreshing(true);
    setOverviewError(null);

    try {
      const response = await axios.get(`${API}/executive-overview`, {
        params: { period_days: targetPeriod }
      });
      setOverviewData(response.data);
      setLastUpdated(new Date());
      if (notify) toast.success("Organization summary refreshed");
    } catch (error) {
      console.error(error);
      setOverviewError("The organization summary could not be loaded. Check the backend service and try again.");
      if (notify) toast.error("Failed to refresh organization summary");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [periodDays]);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  useEffect(() => {
    if (activeTab !== "overview" && !crossData) {
      setCrossLoading(true);
      axios.get(`${API}/cross-function-dashboard`)
        .then((response) => setCrossData(response.data))
        .catch(() => toast.error("Failed to load cross-function data"))
        .finally(() => setCrossLoading(false));
    }
  }, [activeTab, crossData]);

  if (loading) {
    return (
      <div className="space-y-4" data-testid="executive-overview-page">
        <div className="h-36 animate-pulse rounded-2xl border border-slate-200 bg-slate-100" />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {[0, 1, 2, 3].map((item) => <div key={item} className="h-24 animate-pulse rounded-xl bg-slate-100" />)}
        </div>
        <div className="flex items-center justify-center py-10">
          <Loader2 className="h-7 w-7 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  const fin = overviewData?.financial;
  const sc = overviewData?.supply_chain;
  const pu = overviewData?.purchasing;
  const prod = overviewData?.production;
  const finSource = overviewData?.financial_source;
  const scSource = overviewData?.supply_chain_source;
  const puSource = overviewData?.purchasing_source;
  const prodSource = overviewData?.production_source;

  const modules = [];
  if (hasRole("planning_analyst")) {
    modules.push({
      title: "Production Planning", icon: Factory, color: "text-blue-600", bgColor: "bg-blue-50", accentColor: "bg-blue-500",
      path: "/planning", source: prodSource,
      metrics: [
        { label: "Process Orders", value: fmtWithSuffix(prod?.process_orders, ""), icon: Factory },
        { label: "Material Documents", value: fmtWithSuffix(prod?.material_documents, ""), icon: Database },
        { label: "Saved Schedules", value: fmtWithSuffix(prod?.total_schedules, ""), icon: Calendar },
      ],
    });
  }
  if (hasRole("financial_analyst")) {
    modules.push({
      title: "Financial Analysis", icon: Landmark, color: "text-emerald-600", bgColor: "bg-emerald-50", accentColor: "bg-emerald-500",
      path: "/financial-analyst", source: finSource,
      metrics: [
        { label: "Revenue", value: `INR ${fmt(fin?.total_revenue)}`, icon: DollarSign },
        { label: "Net Income", value: `INR ${fmt(fin?.net_income)}`, icon: TrendingUp },
        { label: "Total Assets", value: `INR ${fmt(fin?.total_assets)}`, icon: BarChart3 },
      ],
    });
  }
  if (hasRole("supply_chain_analyst")) {
    modules.push({
      title: "Supply Chain", icon: Package, color: "text-green-600", bgColor: "bg-green-50", accentColor: "bg-green-500",
      path: "/supply-chain", source: scSource,
      metrics: [
        { label: "Stock Value", value: `INR ${fmt(sc?.total_stock_value)}`, icon: Package },
        { label: "Inventory Turnover", value: fmtWithSuffix(sc?.inventory_turnover, "x"), icon: TrendingUp, health: { value: sc?.inventory_turnover, thresholds: [6, 4], type: "higher" } },
        { label: "Inbound OTIF", value: fmtWithSuffix(sc?.otif_inbound, "%"), icon: Truck, health: { value: sc?.otif_inbound, thresholds: [95, 85], type: "higher" } },
        { label: "Stockout Rate", value: fmtWithSuffix(sc?.stockout_rate, "%"), icon: AlertTriangle, health: { value: sc?.stockout_rate, thresholds: [2, 5], type: "lower" } },
        { label: "Fill Rate", value: fmtWithSuffix(sc?.fill_rate, "%"), icon: CheckCircle, health: { value: sc?.fill_rate, thresholds: [98, 95], type: "higher" } },
      ],
    });
  }
  if (hasRole("purchasing_analyst")) {
    modules.push({
      title: "Purchasing", icon: ShoppingCart, color: "text-violet-600", bgColor: "bg-violet-50", accentColor: "bg-violet-500",
      path: "/purchasing", source: puSource,
      metrics: [
        { label: "Total Spend", value: `INR ${fmt(pu?.total_spend)}`, icon: DollarSign },
        { label: "Active Vendors", value: fmtWithSuffix(pu?.active_vendors, ""), icon: Users },
        { label: "PO Compliance", value: fmtWithSuffix(pu?.compliance_rate, "%"), icon: CheckCircle, health: { value: pu?.compliance_rate, thresholds: [90, 80], type: "higher" } },
        { label: "On-Time Delivery", value: fmtWithSuffix(pu?.otd_rate, "%"), icon: Clock, health: { value: pu?.otd_rate, thresholds: [95, 85], type: "higher" } },
        { label: "Quality Rate", value: fmtWithSuffix(pu?.quality_rate, "%"), icon: Activity, health: { value: pu?.quality_rate, thresholds: [98, 95], type: "higher" } },
      ],
    });
  }

  const alerts = [];
  if (sc) {
    const criticalCount = toFiniteNumber(sc.critical_count);
    const belowReorderCount = toFiniteNumber(sc.below_reorder_count);
    const inboundOtif = toFiniteNumber(sc.otif_inbound);
    if (criticalCount != null && criticalCount > 0) alerts.push({ text: `${criticalCount} materials at critical stock`, severity: "critical", module: "Supply Chain", path: "/supply-chain" });
    if (belowReorderCount != null && belowReorderCount > 0) alerts.push({ text: `${belowReorderCount} materials below reorder point`, severity: "warning", module: "Supply Chain", path: "/supply-chain" });
    if (inboundOtif != null && inboundOtif < 90) alerts.push({ text: `Inbound OTIF below 90%: ${inboundOtif}%`, severity: "warning", module: "Supply Chain", path: "/supply-chain" });
  }
  if (pu) {
    const maverickSpend = toFiniteNumber(pu.maverick_spend_rate);
    const vendorOtd = toFiniteNumber(pu.otd_rate);
    const qualityRate = toFiniteNumber(pu.quality_rate);
    if (maverickSpend != null && maverickSpend > 5) alerts.push({ text: `Maverick spend at ${maverickSpend}%`, severity: "warning", module: "Purchasing", path: "/purchasing" });
    if (vendorOtd != null && vendorOtd < 90) alerts.push({ text: `Vendor OTD below 90%: ${vendorOtd}%`, severity: "critical", module: "Purchasing", path: "/purchasing" });
    if (qualityRate != null && qualityRate < 95) alerts.push({ text: `Quality rate below 95%: ${qualityRate}%`, severity: "warning", module: "Purchasing", path: "/purchasing" });
  }

  const sapModules = modules.filter((module) => module.source !== "application_data");
  const liveSapModules = sapModules.filter((module) => module.source === "sap_live").length;
  const companyScope = overviewData?.financial_company_code ? `Company ${overviewData.financial_company_code}` : "Accessible scope";
  const overallStatus = overviewError
    ? { label: "Overview Unavailable", badge: "border-red-200 bg-red-50 text-red-700", icon: WifiOff }
    : sapModules.length === 0
      ? { label: "Application Data", badge: "border-blue-200 bg-blue-50 text-blue-700", icon: Database }
      : liveSapModules === sapModules.length
        ? { label: "SAP Live", badge: "border-emerald-200 bg-emerald-50 text-emerald-700", icon: Wifi }
        : liveSapModules > 0
          ? { label: "Partially Live", badge: "border-amber-200 bg-amber-50 text-amber-700", icon: CircleAlert }
          : { label: "SAP Data Unavailable", badge: "border-red-200 bg-red-50 text-red-700", icon: WifiOff };
  const OverallIcon = overallStatus.icon;
  const summaryCards = [
    hasRole("planning_analyst") && {
      label: "Process Orders", value: fmtWithSuffix(prod?.process_orders, ""), detail: sourceMeta(prodSource).label, icon: Factory, tone: "blue",
    },
    hasRole("supply_chain_analyst") && {
      label: "Inventory Value", value: `INR ${fmt(sc?.total_stock_value)}`, detail: sourceMeta(scSource).label, icon: Package, tone: "emerald",
    },
    hasRole("purchasing_analyst") && {
      label: "Procurement Spend", value: `INR ${fmt(pu?.total_spend)}`, detail: sourceMeta(puSource).label, icon: ShoppingCart, tone: "amber",
    },
    hasRole("financial_analyst") && {
      label: "Total Revenue", value: `INR ${fmt(fin?.total_revenue)}`, detail: sourceMeta(finSource).label, icon: BarChart3, tone: "violet",
    },
  ].filter(Boolean);

  return (
    <div className="space-y-5" data-testid="executive-overview-page">
      <section className="relative isolate overflow-hidden rounded-2xl border border-blue-100 bg-gradient-to-r from-blue-50 via-white to-violet-100/80 px-5 py-6 shadow-sm lg:px-8 lg:py-7">
        <div className="pointer-events-none absolute inset-y-0 right-0 w-[54%] opacity-70" aria-hidden="true">
          <div className="absolute -right-12 -top-20 h-72 w-72 rounded-full bg-violet-200/60 blur-3xl" />
          <div className="absolute right-12 top-12 h-28 w-28 rounded-full bg-sky-200/80 blur-xl" />
          <div className="absolute bottom-0 right-16 h-20 w-44 rounded-t-[3rem] bg-blue-300/40" />
          <div className="absolute bottom-0 right-28 h-32 w-14 rounded-t-xl bg-blue-400/40" />
          <div className="absolute bottom-0 right-44 h-20 w-7 bg-indigo-400/35" />
          <div className="absolute bottom-0 right-56 h-10 w-10 rounded-full bg-emerald-300/50" />
        </div>
        <div className="relative flex flex-col justify-between gap-5 lg:flex-row lg:items-center">
          <div className="flex items-start gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-lg shadow-blue-200">
              <Boxes className="h-7 w-7" />
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-blue-600">Welcome to Mesprosoft</p>
              <h1 className="mt-1 text-2xl font-bold tracking-tight text-slate-950 lg:text-3xl">Organization Health</h1>
              <p className="mt-1 max-w-2xl text-sm text-slate-600 lg:text-base">
                A role-aware summary of live business KPIs, operational exceptions, and module readiness.
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Badge variant="outline" className={`gap-1.5 ${overallStatus.badge}`}>
                  <OverallIcon className="h-3.5 w-3.5" />
                  {overallStatus.label}
                </Badge>
                <span className="flex items-center gap-1 text-xs text-slate-500">
                  <Building2 className="h-3.5 w-3.5" /> {companyScope}
                </span>
                <span className="flex items-center gap-1 text-xs text-slate-500">
                  <Clock className="h-3.5 w-3.5" />
                  {lastUpdated ? `Updated ${lastUpdated.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : "Not refreshed"}
                </span>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2.5 shrink-0">
            <div className="flex items-center gap-1.5 bg-white/90 border border-slate-200/80 rounded-xl px-3 py-1.5 shadow-sm">
              <Calendar className="h-4 w-4 text-blue-600 shrink-0" />
              <select
                value={periodDays}
                onChange={(e) => {
                  const val = Number(e.target.value);
                  setPeriodDays(val);
                  fetchOverview(true, val);
                }}
                className="bg-transparent text-xs font-semibold text-slate-700 focus:outline-none cursor-pointer"
                aria-label="Filter Period"
                data-testid="overview-period-filter"
              >
                <option value={30}>Last 30 Days</option>
                <option value={90}>Last 90 Days</option>
                <option value={180}>Last 180 Days (6 Mos)</option>
                <option value={365}>Last 365 Days (1 Year)</option>
              </select>
            </div>

            <Button variant="outline" className="shrink-0 gap-2 border-white/80 bg-white/90 shadow-sm" onClick={() => fetchOverview(true)} disabled={refreshing} data-testid="overview-refresh">
              {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              {refreshing ? "Refreshing" : "Refresh"}
            </Button>
          </div>
        </div>
      </section>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white p-1 shadow-sm">
          <TabsList className="h-auto min-w-max justify-start bg-transparent" data-testid="overview-tabs">
            <TabsTrigger value="overview" data-testid="overview-tab-summary">Summary</TabsTrigger>
            <TabsTrigger value="scorecard" data-testid="overview-tab-scorecard">KPI Scorecard</TabsTrigger>
            <TabsTrigger value="trends" data-testid="overview-tab-trends">Trend Analysis</TabsTrigger>
            <TabsTrigger value="health" data-testid="overview-tab-health">Health Scores</TabsTrigger>
            <TabsTrigger value="risks" data-testid="overview-tab-risks">Risk Matrix</TabsTrigger>
            <TabsTrigger value="efficiency" data-testid="overview-tab-efficiency">Efficiency</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="overview" className="mt-4 space-y-4">
          {overviewError && (
            <div className="flex flex-col gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800 sm:flex-row sm:items-center sm:justify-between" role="alert">
              <div className="flex items-start gap-2"><CircleAlert className="mt-0.5 h-4 w-4 shrink-0" /><span>{overviewError}</span></div>
              <Button variant="outline" size="sm" className="border-red-200 bg-white" onClick={() => fetchOverview(true)}>Try again</Button>
            </div>
          )}

          {summaryCards.length > 0 && (
            <section className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4" aria-label="Organization summary metrics">
              {summaryCards.map((card) => <SummaryCard key={card.label} {...card} />)}
            </section>
          )}

          {alerts.length > 0 ? (
            <Card className="overflow-hidden border-rose-100 bg-gradient-to-br from-rose-50/85 via-amber-50/70 to-orange-50/80 shadow-sm" data-testid="overview-alerts">
              <CardHeader className="border-b border-amber-100/80 p-5 pb-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-base text-slate-950"><span className="flex h-8 w-8 items-center justify-center rounded-lg bg-rose-100 text-rose-600"><AlertTriangle className="h-4 w-4" /></span> Priority Alerts</CardTitle>
                    <p className="mt-1 text-xs text-slate-500">Threshold exceptions that may require operational action</p>
                  </div>
                  <Badge variant="outline" className="border-orange-200 bg-white/90 px-3 py-1 text-orange-700">{alerts.length} open</Badge>
                </div>
              </CardHeader>
              <CardContent className="grid grid-cols-1 gap-3 p-5 pt-4 lg:grid-cols-2">
                {alerts.map((alert) => (
                  <button type="button" key={`${alert.module}-${alert.text}`} onClick={() => navigate(alert.path)} className={`group flex w-full items-center gap-3 rounded-xl border bg-white/85 p-4 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:bg-white hover:shadow-md ${alert.severity === "critical" ? "border-rose-200 hover:border-rose-300" : "border-amber-200 hover:border-amber-300"}`}>
                    <span className={`h-2.5 w-2.5 shrink-0 rounded-full ring-4 ${alert.severity === "critical" ? "bg-rose-500 ring-rose-100" : "bg-amber-500 ring-amber-100"}`} />
                    <span className="min-w-0 flex-1">
                      <span className={`mb-1 inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${alert.severity === "critical" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`}>{alert.module}</span>
                      <span className="block truncate text-sm font-semibold text-slate-900">{alert.text}</span>
                      <span className="mt-1 block text-xs text-slate-500">Open the module to review and take action.</span>
                    </span>
                    <ArrowRight className={`h-5 w-5 shrink-0 transition-transform group-hover:translate-x-0.5 ${alert.severity === "critical" ? "text-rose-500" : "text-amber-600"}`} />
                  </button>
                ))}
              </CardContent>
            </Card>
          ) : (
            <div className={`flex items-center gap-3 rounded-xl border p-4 ${liveSapModules > 0 ? "border-emerald-200 bg-emerald-50/60" : "border-slate-200 bg-slate-50"}`} data-testid="overview-alerts">
              {liveSapModules > 0 ? <CheckCircle className="h-5 w-5 shrink-0 text-emerald-600" /> : <CircleAlert className="h-5 w-5 shrink-0 text-slate-500" />}
              <div>
                <p className="text-sm font-medium text-slate-900">{liveSapModules > 0 ? "No KPI thresholds currently need attention" : "No live KPI alerts available"}</p>
                <p className="text-xs text-slate-500">{liveSapModules > 0 ? "The currently loaded live indicators are within their configured ranges." : "Activate and verify an SAP connection to evaluate operational thresholds."}</p>
              </div>
            </div>
          )}

          <div className="flex items-end justify-between gap-3 pt-1">
            <div><h2 className="text-base font-semibold text-slate-950">Business Modules</h2><p className="text-xs text-slate-500">Only modules permitted for your account are shown</p></div>
            <Badge variant="secondary" className="text-xs">{modules.length} visible</Badge>
          </div>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2 2xl:grid-cols-4">
            {modules.map((module) => <ModuleCard key={module.title} {...module} onClick={() => navigate(module.path)} />)}
          </div>

          {modules.length === 0 && (
            <Card className="card-industrial border-dashed">
              <CardContent className="p-8 text-center"><Activity className="mx-auto mb-3 h-10 w-10 text-slate-400" /><h3 className="mb-1 text-base font-medium">No Modules Assigned</h3><p className="text-sm text-slate-500">Contact your administrator to get access to analyst modules.</p></CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="scorecard" className="mt-4">
          {crossLoading ? <Loader2 className="mx-auto my-8 h-6 w-6 animate-spin text-primary" /> : <KPIScorecard scorecard={crossData?.scorecard} />}
        </TabsContent>
        <TabsContent value="trends" className="mt-4">
          {crossLoading ? <Loader2 className="mx-auto my-8 h-6 w-6 animate-spin text-primary" /> : <TrendAnalysis trends={crossData?.trends} />}
        </TabsContent>
        <TabsContent value="health" className="mt-4">
          {crossLoading ? <Loader2 className="mx-auto my-8 h-6 w-6 animate-spin text-primary" /> : <HealthScores healthScores={crossData?.health_scores} />}
        </TabsContent>
        <TabsContent value="risks" className="mt-4">
          {crossLoading ? <Loader2 className="mx-auto my-8 h-6 w-6 animate-spin text-primary" /> : <RiskMatrix risks={crossData?.risks} />}
        </TabsContent>
        <TabsContent value="efficiency" className="mt-4">
          {crossLoading ? <Loader2 className="mx-auto my-8 h-6 w-6 animate-spin text-primary" /> : <EfficiencyMetrics efficiency={crossData?.efficiency} />}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ExecutiveOverview;
